import { format, isValid } from 'date-fns'
import React, { useRef, useState, HTMLAttributes, useEffect } from 'react'

type Props = {
    value?: Date
    onValueChange?: (v:Date) => void
    disabled?: boolean
    size?: number
}

const DateInput = ({value, onValueChange, disabled, size, ...props}:Props & HTMLAttributes<HTMLInputElement>) => {

    const inputRef = useRef<HTMLInputElement>(null);
    const [dateString, setDateString] = useState<string>('DD/MM/JJJJ');

    useEffect(()=>{
        onValueChange && onValueChange(new Date(parseInt(dateString.split('/')[2]), (parseInt(dateString.split('/')[1])-1), parseInt(dateString.split('/')[0])))
    }, [dateString])

    useEffect(()=>{
        value && isValid(value) && setDateString(format(value, "dd/MM/yyyy"))
    }, [value])

    const handleClick = (start: number, end: number) => {
        window.setTimeout(()=>{
        inputRef.current && inputRef.current.setSelectionRange(start, end); 
        }, 0) 
    };

    const handleChange = (newValue: string, cursorPos: number) => {
        while (newValue.split('/').length < 3) {
            newValue += '/'; // Append slash to ensure all parts are present
        }
        while (newValue.split('/').length > 3) {
            newValue = newValue.substring(0, newValue.lastIndexOf('/'));
        }
        if (newValue === '') {
            setDateString('DD/MM/JJJJ');
            return;
        }
        
        const parts = newValue.split('/').map((part, index) => {
        let p = part
        
        if (index === 0) {
            part = part.replaceAll('J', 'D')
            part = part.replaceAll('M', 'D')
            // Day part
            if (part.length === 1 && parseFloat(part) > 3) {
                // If the value is single digit and greater than 3, prepend '0'
                p = `0${part}`;
            }
            if (part.length === 2 && parseFloat(part) > 31) {
                // If the value is two digits and greater than 31, truncate to '31'
                p = '31';
            }
            // If day part is removed, replace with 'DD'
            if (part.length === 0) {
                p = 'DD'
            }
            if(part.length > 2){ p = part.substring(0,2) }
            if (!/\d/.test(newValue)) {
                // If day part is completely removed (no digits), replace with 'JJJJ'
                return 'DD';
            }
        }
        else if (index === 1) {
            part = part.replaceAll('J', 'M')
            part = part.replaceAll('D', 'M')
            // Month part
            if (part.length === 1 && parseFloat(part) > 1) {
            // If the value is single digit and greater than 1, prepend '0'
            p = `0${part}`;
            }
            if (part.length === 2 && parseFloat(part) > 12) {
            // If the value is two digits and greater than 12, truncate to '12'
            p = '12';
            }
            // If month part is removed, replace with 'MM'
            if (part.length === 0) {
            p = 'MM';
            }
            if(part.length > 2){ p = part.substring(0,2) }
            if (!/\d/.test(newValue)) {
                // If month part is completely removed (no digits), replace with 'JJJJ'
                return 'MM';
            }
        }
        else if (index === 2) {
            part = part.replaceAll('M', 'J')
            part = part.replaceAll('D', 'J')
            // Year part
            if (part === '0' || part === '00' || part === '000' || part === '0000') {
                // If year part is '0', '00', '000', or '0000', replace with 'JJJJ'
                return 'JJJJ';
            }
            if (!/\d/.test(newValue)) {
                // If year part is completely removed (no digits), replace with 'JJJJ'
                return 'JJJJ';
            }
            if (part.length < 4) {
                // If year part is less than 4 characters, prepend '0's
                return '0'.repeat(4 - part.length) + part;
            }
            if (part.length > 4) {
                // If year part is more than 4 characters, truncate to 4 characters
                return part.slice(-4);
            }
        }
        return p;
        });

        let formattedValue = parts.join('/');

        setDateString(formattedValue);

        if (inputRef.current) {
            // Calculate the new cursor position
            let newPosition = cursorPos + (formattedValue.length - newValue.length);
            let endPosition = newPosition

            // Move cursor to the next segment if the cursor is at the end of a segment
            if (cursorPos === 2 || cursorPos === 5) {
                newPosition++;
                
                if(cursorPos === 2){ endPosition = endPosition+3 }
                if(cursorPos === 5){ endPosition = endPosition+5 }
            }
            
            if(newPosition === endPosition && newPosition === 2){ newPosition = 3; endPosition = 5 }
            else if(newPosition === endPosition && newPosition === 5){ newPosition = 6; endPosition = 10 }
            window.setTimeout(()=>{
                inputRef.current && inputRef.current.setSelectionRange(newPosition, endPosition); 
            }, 0) 
        }
    };

  return (
    <input 
        size={size}
        disabled={disabled}
        ref={inputRef} 
        type="text" 
        value={dateString as string}
        onKeyDown={(e)=>{
            const direction = e.key == 'ArrowLeft' ? 'left' : (e.key === 'ArrowRight' ? 'right' : '')

            const cursorPos = e.currentTarget.selectionStart as number;
            if(cursorPos >= 0 && cursorPos <= 2 && direction === 'right'){ handleClick(3,5); return; }
            else if(cursorPos >= 0 && cursorPos <= 2 && direction === 'left'){ handleClick(0,2); return; }

            else if(cursorPos >= 3 && cursorPos <= 5 && direction === 'right'){ handleClick(6,10); return; }
            else if(cursorPos >= 3 && cursorPos <= 5 && direction === 'left'){ handleClick(0,2); return; }

            else if(cursorPos >= 6 && cursorPos <= 10 && direction === 'right'){ handleClick(6,10); return; }
            else if(cursorPos >= 6 && cursorPos <= 10 && direction === 'left'){ handleClick(3,5); return; }
        }}
        onClick={(e) => {
            const cursorPos = e.currentTarget.selectionStart as number;
            if (cursorPos >= 0 && cursorPos < 2) {
                handleClick(0, 2); // Select "DD"
            } else if (cursorPos >= 2 && cursorPos < 5) {
                handleClick(3, 5); // Select "MM"
            } else if (cursorPos >= 5) {
                handleClick(6, 10); // Select "JJJJ"
            }
        }}
        onChange={(e) => {
            const cursorPos = e.currentTarget.selectionStart as number;
            const newValue = e.target.value;
            handleChange(newValue, cursorPos);
        }}
        {...props}
    />
  )
}

export default DateInput